/**
 * The queue (13-09, RESEARCH Pattern 5): `AnimatePresence mode="popLayout" initial={false}` with
 * `m.li layout`. The tick plays first (the card's `ticked` class), then the card leaves about
 * 250 ms later and the gap closes; an undone card animates back in. `MotionConfig
 * reducedMotion="user"` (main.tsx) plus the global CSS rule make every change instant under
 * reduced motion.
 *
 * Keyboard: when a card leaves with focus inside it, focus moves to the next card's first button
 * (or the one before), so a keyboard user never lands on the page body. The toast's Undo is
 * reachable with Alt+T (Sonner's hotkey); Done today + Reopen is the way back after it closes.
 */

import { useEffect, useRef } from 'react';
import type { MouseEvent as ReactMouseEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';
import { isOverdue } from '../../lib/briefing';
import type { QueueCard } from '../../lib/todayQueue';
import type { CardNote } from '../../hooks/useActionQueue';
import type { Actor } from '../../types/actionRegister';
import { ActionCard } from './ActionCard';
import type { CardHandlers } from './ActionCard';
import styles from './today.module.css';

const cx = (...names: Array<string | false | null | undefined>) => names.filter(Boolean).join(' ');

export interface QueueProps {
  cards: QueueCard[];
  actor: Actor;
  now: number;
  ticking: ReadonlySet<number>;
  notes: Readonly<Record<number, CardNote>>;
  /** Ids accepted (or reopened) this session: the row morphs in place. */
  morphed: ReadonlySet<number>;
  handlers: CardHandlers;
}

const FOCUSABLE = 'button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

export default function Queue({ cards, actor, now, ticking, notes, morphed, handlers }: QueueProps) {
  // Reduced motion: every change is instant (MotionConfig only drops transforms; opacity and the
  // exit would still play), so the cards simply appear and go.
  const reduced = useReducedMotion() ?? false;
  const listRef = useRef<HTMLUListElement | null>(null);
  const lastIndex = useRef<number | null>(null);
  const ids = cards.map((c) => c.row.id).join(',');

  const remember = (e: ReactMouseEvent<HTMLUListElement> | ReactKeyboardEvent<HTMLUListElement>) => {
    const li = (e.target as HTMLElement).closest('li[data-id]');
    if (!li || !listRef.current) return;
    const all = Array.from(listRef.current.querySelectorAll(':scope > li[data-id]'));
    lastIndex.current = all.indexOf(li);
  };

  useEffect(() => {
    const list = listRef.current;
    if (!list || lastIndex.current === null) return;
    const current = new Set(ids.split(','));
    const active = document.activeElement as HTMLElement | null;
    const activeLi = active?.closest<HTMLElement>('li[data-id]');
    const lost = !active || active === document.body;
    const leaving = !!activeLi && list.contains(activeLi) && !current.has(activeLi.dataset.id ?? '');
    if (!lost && !leaving) return; // focus is somewhere on purpose
    const all = Array.from(list.querySelectorAll<HTMLElement>(':scope > li[data-id]')).filter((li) =>
      current.has(li.dataset.id ?? ''),
    );
    if (all.length === 0) return;
    const target = all[Math.min(lastIndex.current, all.length - 1)];
    (target.querySelector<HTMLElement>('[data-primary]') ?? target.querySelector<HTMLElement>(FOCUSABLE))?.focus();
  }, [ids]);

  return (
    <ul ref={listRef} className={styles.queue} aria-label="Your queue" onClickCapture={remember} onKeyDownCapture={remember}>
      <AnimatePresence mode="popLayout" initial={false}>
        {cards.map((card) => {
          const id = card.row.id;
          return (
            <m.li
              key={id}
              data-id={id}
              layout={!reduced}
              className={cx(
                styles.qi,
                card.kind !== 'verify' && isOverdue(card.row, new Date(now)) && styles.overdue,
                ticking.has(id) && styles.ticked,
                morphed.has(id) && styles.accepted,
              )}
              initial={reduced ? false : { opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={reduced ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, x: 24, transition: { duration: 0.22 } }}
              transition={reduced ? { duration: 0 } : { type: 'spring', stiffness: 500, damping: 40 }}
            >
              <ActionCard card={card} actor={actor} now={now} ticked={ticking.has(id)} note={notes[id]} handlers={handlers} />
            </m.li>
          );
        })}
      </AnimatePresence>
    </ul>
  );
}
